import React, { useEffect, useState, useRef } from 'react';
import { Container, Form, Button, Alert, Image, Spinner, Row, Col, FormCheck } from 'react-bootstrap'; // Added Row, Col, FormCheck
import { useNavigate, useParams } from 'react-router-dom';
import { db, storage, auth } from '../../../firebase';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../../components/context/AuthContext';
import { useNotification } from '../../../components/ui/Notification';
import Layout from '../../../components/ui/Layout';
import BreadcrumbNavigation from '../../../components/ui/BreadcrumbNavigation';
import { createLogger } from '../../../utils/logger';
import { DEFAULT_PRICE_TAX_MODE, PRICE_TAX_MODES } from '../../../constants/jamaicaGct';

const logger = createLogger('CreateSupermarket');

// Mobile-responsive styles for opening hours
const openingHoursStyles = `
  .opening-hours-container {
    max-width: 100%;
  }
  
  .opening-hours-item {
    transition: all 0.2s ease-in-out;
    border: 1px solid #e9ecef !important;
    background-color: #f8f9fa !important;
  }
  
  .opening-hours-item:hover {
    border-color: #dee2e6 !important;
    background-color: #ffffff !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .opening-hours-input {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 100% !important;
    box-sizing: border-box !important;
    border: 2px solid #dee2e6 !important;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out !important;
    flex: 1 !important;
  }
  
  .opening-hours-input:focus {
    border-color: #0d6efd !important;
    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25) !important;
    outline: none !important;
  }
  
  .opening-hours-remove-btn {
    border: 2px solid #dc3545 !important;
    color: #dc3545 !important;
    transition: all 0.2s ease-in-out !important;
  }
  
  .opening-hours-remove-btn:hover {
    background-color: #dc3545 !important;
    color: white !important;
    border-color: #dc3545 !important;
  }
  
  .opening-hours-add-btn {
    border: 2px solid #0d6efd !important;
    color: #0d6efd !important;
    transition: all 0.2s ease-in-out !important;
  }
  
  .opening-hours-add-btn:hover {
    background-color: #0d6efd !important;
    color: white !important;
    border-color: #0d6efd !important;
  }
  
  /* Mobile-specific adjustments */
  @media (max-width: 768px) {
    .opening-hours-item {
      padding: 1rem !important;
      margin-bottom: 1rem !important;
    }
    
    .opening-hours-input {
      font-size: 16px !important; /* Prevents zoom on iOS */
      padding: 12px 16px !important;
      margin-bottom: 0.5rem;
      width: 100% !important; /* Ensure full width on mobile */
      max-width: 100% !important;
      min-width: 100% !important;
      box-sizing: border-box !important;
      flex: 1 1 100% !important;
      display: block !important;
    }
    
    /* Override conflicting form-control styles */
    .opening-hours-item .opening-hours-input.form-control {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
    
    .opening-hours-remove-btn {
      width: 100% !important;
      margin-top: 0.5rem;
      padding: 12px 16px !important;
      font-size: 14px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    .opening-hours-add-btn {
      width: 100% !important;
      padding: 12px 24px !important;
      font-size: 16px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    /* Ensure proper stacking on mobile */
    .opening-hours-item .row {
      flex-direction: column !important;
    }
    
    .opening-hours-item .col {
      margin-bottom: 0.5rem !important;
      width: 100% !important;
      flex: 1 1 100% !important;
      max-width: 100% !important;
    }
    
    /* Force input containers to full width */
    .opening-hours-item .col-xs-12,
    .opening-hours-item .col-sm-12,
    .opening-hours-item .col-md-5,
    .opening-hours-item .col-md-2 {
      width: 100% !important;
      max-width: 100% !important;
      flex: 1 1 100% !important;
    }
  }
  
  /* Small mobile screens */
  @media (max-width: 576px) {
    .opening-hours-item {
      padding: 0.75rem !important;
      margin-bottom: 0.75rem !important;
    }
    
    .opening-hours-input {
      padding: 10px 14px !important;
      font-size: 16px !important;
      width: 100% !important; /* Ensure full width on small mobile */
      max-width: 100% !important;
      min-width: 100% !important;
      box-sizing: border-box !important;
      flex: 1 1 100% !important;
      display: block !important;
    }
    
    /* Override conflicting form-control styles */
    .opening-hours-item .opening-hours-input.form-control {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
    
    .opening-hours-remove-btn {
      padding: 10px 14px !important;
      font-size: 13px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    .opening-hours-add-btn {
      padding: 10px 20px !important;
      font-size: 15px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    /* Force all columns to full width on small mobile */
    .opening-hours-item .col {
      width: 100% !important;
      max-width: 100% !important;
      flex: 1 1 100% !important;
    }
  }
  
  /* Extra small screens */
  @media (max-width: 375px) {
    .opening-hours-item {
      padding: 0.5rem !important;
      margin-bottom: 0.5rem !important;
    }
    
    .opening-hours-input {
      padding: 8px 12px !important;
      font-size: 16px !important;
      width: 100% !important; /* Ensure full width on extra small mobile */
      max-width: 100% !important;
      min-width: 100% !important;
      box-sizing: border-box !important;
      flex: 1 1 100% !important;
      display: block !important;
    }
    
    /* Override conflicting form-control styles */
    .opening-hours-item .opening-hours-input.form-control {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
    
    .opening-hours-remove-btn {
      padding: 8px 12px !important;
      font-size: 12px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    .opening-hours-add-btn {
      padding: 8px 16px !important;
      font-size: 14px !important;
      min-height: 44px !important; /* Better touch target */
    }
    
    /* Force all columns to full width on extra small mobile */
    .opening-hours-item .col {
      width: 100% !important;
      max-width: 100% !important;
      flex: 1 1 100% !important;
    }
  }
  
  /* Override conflicting form-control styles at specific breakpoints */
  @media (max-width: 700px) {
    .opening-hours-item .opening-hours-input.form-control {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
  }
  
  @media (max-width: 600px) {
    .opening-hours-item .opening-hours-input.form-control {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
  }
`;

// Inject styles into the document head with duplicate prevention
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('opening-hours-styles');
  if (!existingStyle) {
    const styleElement = document.createElement('style');
    styleElement.id = 'opening-hours-styles';
    styleElement.textContent = openingHoursStyles;
    document.head.appendChild(styleElement);
  }
}

const CreateSupermarket = () => {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { id: supermarketId } = useParams();
  const isEditMode = !!supermarketId;

  const [displayName, setDisplayName] = useState('');
  const [address, setAddress] = useState('');
  const [deliveryPhone, setDeliveryPhone] = useState('');
  const [seoName, setSeoName] = useState('');
  const [status, setStatus] = useState('active');
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  const [maxDeliveryDistance, setMaxDeliveryDistance] = useState('');
  const [fullPaymentEnabled, setFullPaymentEnabled] = useState(false);
  const [showFinancialData, setShowFinancialData] = useState(true);
  const [gctRegistered, setGctRegistered] = useState(false);
  const [trn, setTrn] = useState('');
  const [gctRegistrationNumber, setGctRegistrationNumber] = useState('');
  const [priceTaxMode, setPriceTaxMode] = useState(DEFAULT_PRICE_TAX_MODE);
  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [openingHours, setOpeningHours] = useState([]);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);

  const fileInputRef = useRef();

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { path: "/admin", label: "Admin Panel" },
    { path: "/admin/supermarkets", label: "Supermarkets" },
    { label: isEditMode ? "Edit Supermarket" : "Create Supermarket" }
  ];

  useEffect(() => {
    if (isEditMode) {
      const fetchSupermarket = async () => {
        setPageLoading(true);
        try {
          const docRef = doc(db, 'Supermarkets', supermarketId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setDisplayName(data.display_name || '');
            setAddress(data.address || '');
            setDeliveryPhone(data.delivery_phone || '');
            setSeoName(data.seo_name || '');
            setStatus(data.status || 'active');
            setDeliveryEnabled(data.delivery_enabled || false);
            setDeliveryFee(data.delivery_fee != null && data.delivery_fee !== '' ? String(data.delivery_fee) : '');
            setMaxDeliveryDistance(data.max_delivery_distance != null && data.max_delivery_distance !== '' ? String(data.max_delivery_distance) : '');
            setFullPaymentEnabled(data.full_payment_enabled || false);
            setShowFinancialData(data.show_financial_data !== undefined ? data.show_financial_data : true);
            setGctRegistered(data.gct_registered || false);
            setTrn(data.trn || '');
            setGctRegistrationNumber(data.gct_registration_number || '');
            setPriceTaxMode(data.price_tax_mode || DEFAULT_PRICE_TAX_MODE);
            setExistingMedia(data.media || []);
            setOpeningHours(data.opening_hours || []);
          } else {
            setError('Supermarket not found.');
          }
        } catch (err) {
          setError(`Failed to load supermarket data. ${  err.message}`);
          logger.error(err);
        }
        setPageLoading(false);
      };
      fetchSupermarket();
    }
  }, [supermarketId, isEditMode]); // Removed navigate from deps as it's stable

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate files before setting state
    const validFiles = selectedFiles.filter(file => {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showNotification({ type: 'warning', title: 'File Too Large', message: `File ${file.name} is too large. Maximum size is 5MB.` });
        return false;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        showNotification({ type: 'warning', title: 'Invalid File Type', message: `File ${file.name} is not an image. Please select image files only.` });
        return false;
      }
      
      // Check for problematic filenames
      if (file.name.includes('localhost_3000') || 
          file.name.includes('cart_instance_') || 
          file.name.includes('order-status')) {
        showNotification({ type: 'warning', title: 'Invalid Filename', message: `File ${file.name} has a problematic filename. Please rename the file before uploading.` });
        return false;
      }
      
      return true;
    });
    
    setFiles(validFiles);
  };

  const handleRemoveExistingImage = (indexToRemove, _mediaItem) => {
    setExistingMedia(prev => prev.filter((_, index) => index !== indexToRemove));
    showNotification({ type: 'info', title: 'Image Removed', message: 'Image marked for removal. Deletion from storage will occur on save.' });
  };

  const handleRemoveNewImage = (indexToRemove) => {
    setFiles(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleAddOpeningHour = () => {
    setOpeningHours(prev => [...prev, { day: '', hours: '' }]);
  };

  const handleRemoveOpeningHour = (indexToRemove) => {
    setOpeningHours(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleOpeningHourChange = (index, field, value) => {
    setOpeningHours(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const uploadFiles = async () => {
    const uploadedMediaObjects = [];
    const uniqueIdPart = supermarketId || Date.now(); // Use supermarketId if editing
    
    for (const file of files) {
      try {
        // Better filename sanitization
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const timestamp = Date.now();
        const gcsPath = `images/supermarkets/${uniqueIdPart}/${timestamp}_${sanitizedName}`;
        
        // Ensure we have authentication before upload
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('User not authenticated');
        }
        
        // Refresh the user's token to ensure it's valid
        try {
          await currentUser.getIdToken(true);
 
        } catch (tokenError) {
          logger.error('Token refresh failed:', tokenError);
          throw new Error('Authentication token refresh failed');
        }
        
        const fileRef = ref(storage, gcsPath);
        const uploadTask = uploadBytesResumable(fileRef, file);

        await new Promise((resolve, _reject) => {
          uploadTask.on(
            'state_changed',
                         (_snapshot) => { 
               // Progress tracking - removed unused variable
             },
            (error) => { 
              logger.error('Upload error for file:', file.name, error); 
              logger.error('Error details:', {
                code: error.code,
                message: error.message,
                serverResponse: error.serverResponse
              });
              
              
              
              // Fallback: use local public fallback image
              uploadedMediaObjects.push({
                name: 'fallback-supermarket.svg',
                url: '/fallback-product.svg',
                path: 'public/fallback-product.svg',
                type: 'image/svg+xml',
                size: 0,
                created_at: Timestamp.fromDate(new Date())
              });
              resolve(); // Do not reject, continue with fallback
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                uploadedMediaObjects.push({
                  name: file.name,
                  url: downloadURL,
                  path: gcsPath, 
                  type: file.type,
                  size: file.size,
                  created_at: Timestamp.fromDate(new Date()),
                });
                
                resolve();
              } catch (urlError) { 
                logger.error('Error getting download URL for file:', file.name, urlError);
                // Fallback: use local public fallback image
                uploadedMediaObjects.push({
                  name: 'fallback-supermarket.svg',
                  url: '/fallback-product.svg',
                  path: 'public/fallback-product.svg',
                  type: 'image/svg+xml',
                  size: 0,
                  created_at: Timestamp.fromDate(new Date())
                });
                resolve();
              }
            }
          );
        });
      } catch (error) {
        logger.error('Unexpected error during upload:', error);
        // Fallback for unexpected errors
        uploadedMediaObjects.push({
          name: 'fallback-supermarket.svg',
          url: '/fallback-product.svg',
          path: 'public/fallback-product.svg',
          type: 'image/svg+xml',
          size: 0,
          created_at: Timestamp.fromDate(new Date())
        });
      }
    }
    return uploadedMediaObjects;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!displayName || !address) { setError('Please fill in all required fields.'); return; }
    
    // Check authentication before upload
    if (!currentUser) {
      setError('You must be logged in to upload files.');
      return;
    }
    
    setLoading(true); setError(''); setSuccess('');
    try {
             // Force token refresh to ensure latest permissions
       await currentUser.getIdToken(true);
      
      const newUploadedMedia = await uploadFiles();
      const finalMedia = [...existingMedia, ...newUploadedMedia];
      
      // Filter out empty opening hours entries
      const validOpeningHours = openingHours.filter(oh => oh.day && oh.hours);
      
      const parsedDeliveryFee = deliveryFee !== '' ? parseFloat(deliveryFee) : null;
      const parsedMaxDeliveryDistance = maxDeliveryDistance !== '' ? parseFloat(maxDeliveryDistance) : null;

      if (parsedDeliveryFee != null && (isNaN(parsedDeliveryFee) || parsedDeliveryFee < 0)) {
        setError('Delivery fee must be a valid non-negative number.');
        setLoading(false);
        return;
      }
      if (parsedMaxDeliveryDistance != null && (isNaN(parsedMaxDeliveryDistance) || parsedMaxDeliveryDistance <= 0)) {
        setError('Max delivery distance must be a valid positive number.');
        setLoading(false);
        return;
      }

      const supermarketData = {
        display_name: displayName, 
        address, 
        delivery_phone: deliveryPhone,
        seo_name: seoName,
        status, 
        delivery_enabled: deliveryEnabled,
        delivery_fee: parsedDeliveryFee,
        max_delivery_distance: parsedMaxDeliveryDistance,
        full_payment_enabled: fullPaymentEnabled,
        show_financial_data: showFinancialData,
        gct_registered: gctRegistered,
        trn: trn.trim() || null,
        gct_registration_number: gctRegistered ? (gctRegistrationNumber.trim() || null) : null,
        price_tax_mode: priceTaxMode || DEFAULT_PRICE_TAX_MODE,
        media: finalMedia,
        opening_hours: validOpeningHours,
        lastUpdatedOn: Timestamp.fromDate(new Date()),
        lastUpdatedBy: { uid: currentUser.uid, email: currentUser.email }
      };
      if (isEditMode) {
        const docRef = doc(db, 'Supermarkets', supermarketId);
        await updateDoc(docRef, supermarketData);
        setSuccess('Supermarket updated successfully!');
      } else {
        supermarketData.created_at = Timestamp.fromDate(new Date());
        supermarketData.created_by = { uid: currentUser.uid, email: currentUser.email };
        
                 await addDoc(collection(db, 'Supermarkets'), supermarketData);
        setSuccess('Supermarket created successfully!');
        setDisplayName(''); setAddress(''); setDeliveryPhone(''); setSeoName(''); setStatus('active'); setDeliveryEnabled(false); setDeliveryFee(''); setMaxDeliveryDistance(''); setFullPaymentEnabled(false); setShowFinancialData(true); setGctRegistered(false); setTrn(''); setGctRegistrationNumber(''); setPriceTaxMode(DEFAULT_PRICE_TAX_MODE); setFiles([]); setOpeningHours([]);
        if(fileInputRef.current) fileInputRef.current.value = '';
      }
      setTimeout(() => navigate('/admin/supermarkets'), 1500);
    } catch (err) {
      setError(`Operation failed: ${  err.message}`); logger.error(err);
    }
    setLoading(false);
  };

  const handleGoBack = () => navigate(-1);





  if (pageLoading && isEditMode) {
    return <Layout><Container className="text-center mt-5"><Spinner animation="border" /> <p>Loading supermarket data...</p></Container></Layout>;
  }

  return (
    <Layout>
      <Container className="mt-3 px-3">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={handleGoBack} className="mb-2">
                &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        
        <h1 className="text-break mb-3">{isEditMode ? 'Edit Supermarket' : 'Create Supermarket'}</h1>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit} className="w-100">
          <Form.Group className="mb-3" controlId="formDisplayName">
            <Form.Label className="fw-semibold">Supermarket Name</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter supermarket name" 
              value={displayName} 
              onChange={(e) => setDisplayName(e.target.value)} 
              required 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="formAddress">
            <Form.Label className="fw-semibold">Address</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter address" 
              value={address} 
              onChange={(e) => setAddress(e.target.value)} 
              required 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formDeliveryPhone">
            <Form.Label className="fw-semibold">Delivery Phone Number</Form.Label>
            <Form.Control 
              type="tel" 
              placeholder="Enter delivery phone number (e.g., +1-555-123-4567)" 
              value={deliveryPhone} 
              onChange={(e) => setDeliveryPhone(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
            <Form.Text className="text-muted small">
              Phone number for delivery inquiries and coordination.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formSeoName">
            <Form.Label className="fw-semibold">SEO Name</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter SEO-friendly name (e.g., test-mart-grocery)" 
              value={seoName} 
              onChange={(e) => setSeoName(e.target.value)} 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
            <Form.Text className="text-muted small">
              URL-friendly name for better search engine optimization. Use lowercase letters, numbers, and hyphens only.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3" controlId="formStatus">
            <Form.Label className="fw-semibold">Status</Form.Label>
            <Form.Select 
              value={status} 
              onChange={(e) => setStatus(e.target.value)}
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formGctRegistered">
            <FormCheck
              type="checkbox"
              id="gctRegistered"
              label="GCT Registered"
              checked={gctRegistered}
              onChange={(e) => setGctRegistered(e.target.checked)}
              className="mb-2"
            />
            <Form.Text className="text-muted small">
              Enable when this supermarket is registered to collect Jamaica General Consumption Tax.
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formTrn">
                <Form.Label className="fw-semibold">TRN</Form.Label>
                <Form.Control
                  type="text"
                  inputMode="numeric"
                  placeholder="Enter Taxpayer Registration Number"
                  value={trn}
                  onChange={(e) => setTrn(e.target.value)}
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                <Form.Text className="text-muted small">
                  Store the supermarket TRN for tax records and receipts.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formGctRegistrationNumber">
                <Form.Label className="fw-semibold">GCT Registration Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter GCT registration number"
                  value={gctRegistrationNumber}
                  onChange={(e) => setGctRegistrationNumber(e.target.value)}
                  disabled={!gctRegistered}
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                <Form.Text className="text-muted small">
                  Required only for supermarkets registered to collect GCT.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3" controlId="formPriceTaxMode">
            <Form.Label className="fw-semibold">Price Tax Mode</Form.Label>
            <Form.Select
              value={priceTaxMode}
              onChange={(e) => setPriceTaxMode(e.target.value)}
              className="w-100"
              style={{
                fontSize: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            >
              {PRICE_TAX_MODES.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted small">
              Defaults to tax-inclusive so inventory staff enter the shelf price customers see.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formDeliveryEnabled">
            <FormCheck
              type="checkbox"
              id="deliveryEnabled"
              label="Enable Delivery Service"
              checked={deliveryEnabled}
              onChange={(e) => setDeliveryEnabled(e.target.checked)}
              className="mb-2"
            />
            <Form.Text className="text-muted small">
              When enabled, customers can choose delivery option for orders from this supermarket.
            </Form.Text>
          </Form.Group>

          {deliveryEnabled && (
            <>
              <Form.Group className="mb-3" controlId="formDeliveryFee">
                <Form.Label className="fw-semibold">Delivery Fee (JMD)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g., 500"
                  value={deliveryFee}
                  onChange={(e) => setDeliveryFee(e.target.value)}
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                <Form.Text className="text-muted small">
                  Fixed delivery fee charged at checkout when the customer selects home delivery. Leave blank if fees are arranged directly with the customer.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formMaxDeliveryDistance">
                <Form.Label className="fw-semibold">Max Delivery Distance (km)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="e.g., 10"
                  value={maxDeliveryDistance}
                  onChange={(e) => setMaxDeliveryDistance(e.target.value)}
                  className="w-100"
                  style={{
                    fontSize: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #dee2e6',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                />
                <Form.Text className="text-muted small">
                  Maximum delivery radius from the supermarket. Shown to customers at checkout.
                </Form.Text>
              </Form.Group>
            </>
          )}

          <Form.Group className="mb-3" controlId="formFullPaymentEnabled">
            <FormCheck
              type="checkbox"
              id="fullPaymentEnabled"
              label="Enable Full Payment Option"
              checked={fullPaymentEnabled}
              onChange={(e) => setFullPaymentEnabled(e.target.checked)}
              className="mb-2"
            />
            <Form.Text className="text-muted small">
              When enabled, customers can choose to pay the full order total (including subtotal and confirmation fee as processing fee) instead of just the confirmation fee.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formShowFinancialData">
            <FormCheck
              type="checkbox"
              id="showFinancialData"
              label="Show Financial Dashboard Data"
              checked={showFinancialData}
              onChange={(e) => setShowFinancialData(e.target.checked)}
              className="mb-2"
            />
            <Form.Text className="text-muted small">
              When enabled, employees will see financial dashboard data (Full Payment Orders, Confirmation Fee Only, and Total Revenue) on the Employee Dashboard and Manage Packed Orders pages. When disabled, this sensitive financial information will be hidden.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formOpeningHours">
            <Form.Label className="fw-semibold">Opening Hours</Form.Label>
            <Form.Text className="text-muted small d-block mb-3">
              Add the days and hours when the supermarket is open. You can enter single days (e.g., "Monday") or ranges (e.g., "Monday - Friday", "Weekdays", "Weekend").
            </Form.Text>
            
            {openingHours.length > 0 && (
              <div className="mb-3 opening-hours-container">
                {openingHours.map((oh, index) => (
                  <div key={index} className="opening-hours-item mb-3 p-3 border rounded bg-light">
                    <Row className="align-items-end">
                      <Col xs={12} md={5} className="mb-2 mb-md-0">
                        <Form.Label className="small mb-1 fw-semibold">Day(s)</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., Monday - Friday, Weekdays"
                          value={oh.day}
                          onChange={(e) => handleOpeningHourChange(index, 'day', e.target.value)}
                          className="opening-hours-input"
                          style={{ 
                            fontSize: '16px', // Prevents zoom on iOS
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #dee2e6',
                            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                          list={`day-suggestions-${index}`}
                        />
                        <datalist id={`day-suggestions-${index}`}>
                          <option value="Monday" />
                          <option value="Tuesday" />
                          <option value="Wednesday" />
                          <option value="Thursday" />
                          <option value="Friday" />
                          <option value="Saturday" />
                          <option value="Sunday" />
                          <option value="Monday - Friday" />
                          <option value="Monday - Saturday" />
                          <option value="Saturday - Sunday" />
                          <option value="Weekdays" />
                          <option value="Weekend" />
                          <option value="Daily" />
                        </datalist>
                      </Col>
                      <Col xs={12} md={5} className="mb-2 mb-md-0">
                        <Form.Label className="small mb-1 fw-semibold">Hours</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., 9:00 AM - 5:00 PM"
                          value={oh.hours}
                          onChange={(e) => handleOpeningHourChange(index, 'hours', e.target.value)}
                          className="opening-hours-input"
                          style={{ 
                            fontSize: '16px', // Prevents zoom on iOS
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: '2px solid #dee2e6',
                            transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box'
                          }}
                        />
                      </Col>
                      <Col xs={12} md={2} className="text-center text-md-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveOpeningHour(index)}
                          className="opening-hours-remove-btn"
                          style={{ 
                            padding: '12px 16px',
                            fontSize: '14px',
                            borderRadius: '8px',
                            minWidth: '80px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-trash-alt me-1"></i>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ))}
              </div>
            )}
            
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleAddOpeningHour}
              className="opening-hours-add-btn"
              style={{ 
                padding: '12px 24px',
                fontSize: '16px',
                borderRadius: '8px',
                fontWeight: '500'
              }}
            >
              <i className="fas fa-plus me-2"></i>
              Add Opening Hours
            </Button>
          </Form.Group>

          <Form.Group controlId="formFiles" className="mb-3">
            <Form.Label className="fw-semibold">Upload Images (Max 5)</Form.Label>
            <Form.Control 
              type="file" 
              multiple 
              onChange={handleFileChange} 
              ref={fileInputRef} 
              accept="image/*" 
              className="w-100"
              style={{ 
                fontSize: '16px', // Prevents zoom on iOS
                padding: '12px 16px',
                borderRadius: '8px',
                border: '2px solid #dee2e6',
                transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />

          </Form.Group>

          {isEditMode && existingMedia.length > 0 && (
            <div className="mb-3">
              <h6 className="fw-semibold">Current Images:</h6>
              <div className="d-flex flex-wrap gap-2">
                {existingMedia.map((media, index) => (
                  <div key={`existing-${index}`} className="border p-2 rounded" style={{ minWidth: '120px', maxWidth: '150px' }}>
                    <Image 
                      src={media.url} 
                      alt={media.name || `Existing Image ${index+1}`} 
                      thumbnail 
                      style={{maxWidth: '100px', maxHeight: '100px', width: '100%', height: 'auto'}} 
                      className="img-fluid"
                    />
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="mt-2 w-100" 
                      onClick={() => handleRemoveExistingImage(index, media)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className="mb-3">
              <h6 className="fw-semibold">New Images to Upload:</h6>
              <div className="d-flex flex-wrap gap-2">
                {files.map((file, index) => (
                  <div key={`new-${index}`} className="border p-2 rounded" style={{ minWidth: '120px', maxWidth: '150px' }}>
                    <Image 
                      src={URL.createObjectURL(file)} 
                      alt={file.name} 
                      thumbnail 
                      style={{maxWidth: '100px', maxHeight: '100px', width: '100%', height: 'auto'}} 
                      className="img-fluid"
                    />
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="mt-2 w-100" 
                      onClick={() => handleRemoveNewImage(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || (isEditMode && pageLoading)}
            className="w-100 w-md-auto"
            size="lg"
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Processing...
              </>
            ) : (
              isEditMode ? 'Update Supermarket' : 'Create Supermarket'
            )}
          </Button>
        </Form>
      </Container>
    </Layout>
  );
};

export default CreateSupermarket;
